import gsap from 'gsap'
import { params } from '../config/persistence.js'

export const isDebug = new URLSearchParams(window.location.search).get('debug') === 'true'

const MENU_ITEMS = ['Brera', 'Zero', 'Quantum Pro', 'Level One', 'Novità', 'Novità']

const splitChars = (el, text) => {
    el.textContent = ''
    const wrappers = []
    const inners = []
    for (const c of text) {
        const wrapper = document.createElement('span')
        wrapper.className = 'falmec-hero-char'
        if (c === ' ') wrapper.classList.add('falmec-hero-space')
        const inner = document.createElement('span')
        inner.className = 'falmec-hero-char-inner'
        inner.textContent = c === ' ' ? ' ' : c
        wrapper.appendChild(inner)
        el.appendChild(wrapper)
        wrappers.push(wrapper)
        inners.push(inner)
    }
    return { wrappers, inners }
}

export const createOverlay = () => {
    // --- Edge gradients (left + right) ---
    const edgeLeft = document.createElement('div')
    edgeLeft.className = 'falmec-edge-fade falmec-edge-fade-left'
    document.body.appendChild(edgeLeft)
    const edgeRight = document.createElement('div')
    edgeRight.className = 'falmec-edge-fade falmec-edge-fade-right'
    document.body.appendChild(edgeRight)

    // --- Top bar ---
    const topBar = document.createElement('header')
    topBar.className = 'falmec-topbar'
    topBar.innerHTML = `
        <div class="falmec-lang">
            <svg class="falmec-lang-icon" viewBox="0 0 10 7" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 1.5l4 4 4-4"/>
            </svg>
            <span>iT</span>
        </div>
        <a class="falmec-brand" href="https://falmec.com" target="_blank" rel="noopener">
            <span>falmec.com</span>
            <span class="falmec-arrow-box" aria-hidden="true">
                <svg viewBox="0 0 8 8" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M2 6 L6 2"/>
                    <path d="M3 2 H6 V5"/>
                </svg>
            </span>
        </a>
        <nav class="falmec-menu" aria-label="Prodotti">
            ${MENU_ITEMS.map(item => `<a href="#" class="falmec-menu-item">${item}</a>`).join('')}
        </nav>
    `
    document.body.appendChild(topBar)

    // --- Hero title (char split with mask reveal) ---
    const hero = document.createElement('h1')
    hero.className = 'falmec-hero'
    document.body.appendChild(hero)
    const { inners: chars } = splitChars(hero, 'Cooking systems')

    // --- Bottom right paragraph ---
    const desc = document.createElement('p')
    desc.className = 'falmec-desc'
    desc.innerHTML = `<span class="falmec-desc-indent"></span>Un spazio dove si respira un'atmosfera innovativa. Assaporando di nuovo il piacere di stare in cucina.`
    document.body.appendChild(desc)

    // --- Bottom left caption ---
    const caption = document.createElement('div')
    caption.className = 'falmec-caption'
    caption.innerHTML = `
        <span class="falmec-caption-label">Sistemi di cottura e aspirazione integrata</span>
        <span class="falmec-arrow-box falmec-arrow-down" aria-hidden="true">
            <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 2 V8"/>
                <path d="M2 5 L5 8 L8 5"/>
            </svg>
        </span>
    `
    document.body.appendChild(caption)

    // --- Initial states ---
    gsap.set([topBar, desc, caption], { opacity: 0, y: 8 })
    gsap.set(chars, { yPercent: 130 })

    // --- Particle spawn rate ramp ---
    const targetSpawn = params.respawnRate
    const spawnState = { value: 0 }
    const compute = window._getCompute && window._getCompute()
    if (compute) compute.particlesVariable.material.uniforms.uDecayRate.value = 0

    // In debug, skip the intro animation and jump to the final state
    if (isDebug) {
        gsap.set([topBar, desc, caption], { opacity: 1, y: 0 })
        gsap.set(chars, { yPercent: 0 })
        window._fadeState.value = 1.0
        if (compute) compute.particlesVariable.material.uniforms.uDecayRate.value = targetSpawn
        params.suctionBarsEnabled = true
        return
    }

    const heroStart = 0.05
    const heroDuration = 0.9
    const heroStagger = 0.022
    const heroEnd = heroStart + (chars.length - 1) * heroStagger + heroDuration
    const spawnRampDuration = heroEnd - heroStart
    const particleFadeDuration = 43.2
    const particleFadeStart = 0.75
    const descStart = heroEnd - 0.6
    const descDuration = 1.2
    const uiEnd = descStart + descDuration
    const suctionStartTime = uiEnd

    const tl = gsap.timeline({ delay: 0.4 })

    tl.to(topBar, { opacity: 1, y: 0, duration: 1.0, ease: 'power3.out' }, 0)
        .to(window._fadeState, {
            value: 1.0,
            duration: particleFadeDuration,
            ease: 'expo.out',
        }, particleFadeStart)
        .to(chars, {
            yPercent: 0,
            duration: heroDuration,
            ease: 'expo.out',
            stagger: heroStagger,
        }, heroStart)
        .to(spawnState, {
            value: targetSpawn,
            duration: spawnRampDuration,
            ease: 'power2.out',
            onUpdate: () => {
                const c = window._getCompute && window._getCompute()
                if (c) c.particlesVariable.material.uniforms.uDecayRate.value = spawnState.value
            },
        }, heroStart)
        .to(desc, { opacity: 1, y: 0, duration: descDuration, ease: 'power3.out' }, descStart)
        .to(caption, { opacity: 1, y: 0, duration: 1.0, ease: 'power3.out' }, descStart)
        .add(() => { params.suctionBarsEnabled = true }, suctionStartTime)
}
